import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import TranscriptsList, { TranscriptsListProps } from './components/TranscriptsList';
import CollectionsList from './components/CollectionsList';

import { grpc } from '@improbable-eng/grpc-web';
import transcript_pb from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import transcript_pb_grpc, { ServiceError } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb_service';
import { FieldMask } from 'google-protobuf/google/protobuf/field_mask_pb';
import { TRANSCRIPT_SERVICE_HOST } from './config';
import { withAuth, AuthContextProps } from './LocalAuthWrapper';
import { isTransientGrpcError } from './helpers';
import TranscriptService from './TranscriptService';
import './TranscriptsListWrapper.css';
import { Empty } from 'google-protobuf/google/protobuf/empty_pb';
import { Modal, Select, Button, Space, message, Input, List, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

type TranscriptsListWrapperState = {
  transcriptNames: string[];
  transcripts: transcript_pb.Transcript.AsObject[];
  sortBy: TranscriptsListProps['sortBy'];
  sortDirection: TranscriptsListProps['sortDirection'];
  deleteError: boolean;
  collections: transcript_pb.Collection.AsObject[];
  selectedTranscriptKeys: React.Key[];
  isAddToCollectionModalVisible: boolean;
  targetCollectionName: string | null;
  loadingCollections: boolean;
  isShareModalVisible: boolean;
  sharingCollection: transcript_pb.Collection.AsObject | null;
  sharingActors: transcript_pb.GetCollectionPermissionsResponse.Actor.AsObject[];
  newSharingEmail: string;
  loadingSharing: boolean;
};

type TranscriptsListWrapperProps = {
  filter?: string;
  collectionName?: string;
} & AuthContextProps &
  RouteComponentProps;

class TranscriptsListWrapper extends React.Component<TranscriptsListWrapperProps, TranscriptsListWrapperState> {
  state: TranscriptsListWrapperState = {
    transcriptNames: [],
    transcripts: [],
    sortBy: 'versionTime',
    sortDirection: 'descend',
    deleteError: false,
    collections: [],
    selectedTranscriptKeys: [],
    isAddToCollectionModalVisible: false,
    targetCollectionName: null,
    loadingCollections: false,
    isShareModalVisible: false,
    sharingCollection: null,
    sharingActors: [],
    newSharingEmail: '',
    loadingSharing: false,
  };
  transcriptService: TranscriptService;
  stream?: transcript_pb_grpc.ResponseStream<transcript_pb.WatchTranscriptsResponse>;

  constructor(props: TranscriptsListWrapperProps) {
    super(props);
    this.transcriptService = new TranscriptService({
      getIdToken: () => this.props.authCtx.accessToken,
    });
  }

  showError = (defaultMsg: string, error?: any) => {
    if (error && isTransientGrpcError(error)) {
      console.warn(`Suppressing transient gRPC error: ${defaultMsg}`, error);
      return;
    }
    message.error(defaultMsg);
  };

  updateTranscriptMetadata = (name: string, metadata: transcript_pb.TranscriptMetadata.AsObject) => {
    this.transcriptService
      .updateTranscriptMetadata(name, metadata)
      .then(() => {
        message.success('Lýsigögn uppfærð');
      })
      .catch((error) => {
        this.showError('Ekki tókst að uppfæra lýsigögn upptöku', error);
        console.error('updateTranscriptMetadata error', error);
      });
  };

  deleteTranscript = (transcript: transcript_pb.Transcript.AsObject) => {
    this.transcriptService
      .deleteTranscript(transcript.name)
      .then(() => {
        message.success('Upptöku eytt');
        this.setState((prevState) => {
          return {
            transcripts: prevState.transcripts.filter((t) => t.name !== transcript.name),
          };
        });
      })
      .catch((error) => {
        this.showError('Ekki tókst að eyða upptöku', error);
        console.error('deleteTranscript error', error);
        this.setState({ deleteError: true });
      });
  };

  handleRemoveFromCollection = (transcript: transcript_pb.Transcript.AsObject) => {
    const { collectionName } = this.props;
    if (!collectionName) return;

    this.transcriptService
      .removeTranscriptFromCollection(collectionName, transcript.name)
      .then(() => {
        message.success('Upptaka fjarlægð úr safni');
        this.setState((prevState) => ({
          transcripts: prevState.transcripts.filter((t) => t.name !== transcript.name),
        }));
      })
      .catch((error) => {
        this.showError('Ekki tókst að fjarlægja upptöku úr safni', error);
        console.error('removeTranscriptFromCollection error', error);
      });
  };

  cancelStream = () => {
    if (this.stream) {
      try {
        this.stream.cancel();
      } catch (e) {
        console.debug('Error cancelling stream', e);
      }
      this.stream = undefined;
    }
  };

  watchTranscripts = () => {
    this.cancelStream();
    this.setState({ transcripts: [] });
    const { filter, collectionName } = this.props;
    let filterExpr: null | string = null;

    if (filter && filter !== '') {
      if (filter.startsWith('merki:')) {
        const keywordsList = filter
          .substr(6)
          .split(',')
          .map((v) => `"${v}"`)
          .join(', ');
        filterExpr = `metadata.keywords CONTAINS [${keywordsList}]`;
      } else if (filter.startsWith('!merki:')) {
        const keywordsList = filter
          .substr(7)
          .split(',')
          .map((v) => `"${v}"`)
          .join(', ');
        filterExpr = `metadata.keywords NCONTAINS [${keywordsList}]`;
      } else {
        filterExpr = `metadata.subject CONTAINS "${filter}"`;
      }
    }

    const req = new transcript_pb.WatchTranscriptsRequest();
    if (filterExpr) {
      req.setFilter(filterExpr);
    }

    req.setPageSize(600);
    if (collectionName) {
      req.setCollectionName(collectionName);
      req.setExcludeCollections(false);
    } else {
      req.setExcludeCollections(true);
    }

    this.stream = this.transcriptService.watchTranscripts(req);

    this.stream.on('data', (msg: transcript_pb.WatchTranscriptsResponse) => {
      const incomingTranscripts = msg
        .getTranscriptsList()
        .map((transcript) => transcript.toObject())
        .filter((el) => !!el);

      this.setState((prevState) => {
        const transcriptMap = new Map<string, transcript_pb.Transcript.AsObject>();
        prevState.transcripts.forEach((t) => transcriptMap.set(t.name, t));
        incomingTranscripts.forEach((t) => transcriptMap.set(t.name, t));

        return {
          transcripts: Array.from(transcriptMap.values()),
        };
      });
    });
    this.stream.on('end', () => console.debug('stream ended'));
    this.stream.on('status', (status: transcript_pb_grpc.Status) => {
      console.error('watchTranscripts error', status);
      console.debug('stream status');
    });
  };

  listTranscripts = () => {
    const { collectionName } = this.props;
    const req = new transcript_pb.ListTranscriptsRequest();
    if (collectionName) {
      req.setCollectionName(collectionName);
      req.setExcludeCollections(false);
    } else {
      req.setExcludeCollections(true);
    }
    this.transcriptService
      .listTranscripts(req)
      .then((transcripts) => {
        const incomingTranscripts = transcripts
          .map((transcript) => transcript.toObject())
          .filter((transcript) => !!transcript);

        this.setState((prevState) => {
          const transcriptMap = new Map<string, transcript_pb.Transcript.AsObject>();
          prevState.transcripts.forEach((t) => transcriptMap.set(t.name, t));
          incomingTranscripts.forEach((t) => transcriptMap.set(t.name, t));

          return {
            transcripts: Array.from(transcriptMap.values()),
          };
        });
      })
      .catch((error) => {
        console.error('listTranscripts error', error);
      });
  };
  handleShareCollection = (collection: transcript_pb.Collection.AsObject) => {
    this.setState({
      isShareModalVisible: true,
      sharingCollection: collection,
      loadingSharing: true,
      sharingActors: [],
    });

    this.transcriptService
      .getCollectionPermissions(collection.name)
      .then((actorNames) => {
        this.setState({
          sharingActors: actorNames,
          loadingSharing: false,
        });
      })
      .catch((error) => {
        this.setState({ loadingSharing: false });
        console.error('getCollectionPermissions error', error);
      });
  };

  handleAddSharingActor = () => {
    const { newSharingEmail, sharingActors, sharingCollection } = this.state;
    const input = newSharingEmail.trim();
    if (!input || !sharingCollection) return;

    // Validation: email or 10-digit kennitala
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
    const isKennitala = /^\d{10}$/.test(input);

    if (!isEmail && !isKennitala) {
      message.error('Vinsamlegast sláðu inn gilt netfang eða 10 stafa kennitölu');
      return;
    }

    const updatedActorNames = [...sharingActors.map((a) => a.name), input];
    this.setState({ loadingSharing: true });
    this.transcriptService
      .setCollectionPermissions(sharingCollection.name, updatedActorNames)
      .then(() => {
        message.success('Eiganda bætt við');
        this.setState({
          newSharingEmail: '',
        });
        this.handleShareCollection(sharingCollection); // Refresh to get display names
      })
      .catch((error) => {
        this.setState({ loadingSharing: false });
        this.showError('Ekki tókst að bæta við eiganda', error);
      });
  };

  handleRemoveSharingActor = (actorName: string) => {
    const { sharingActors, sharingCollection } = this.state;
    if (!sharingCollection) return;

    const updatedActorNames = sharingActors.map((a) => a.name).filter((name) => name !== actorName);
    this.setState({ loadingSharing: true });
    this.transcriptService
      .setCollectionPermissions(sharingCollection.name, updatedActorNames)
      .then(() => {
        message.success('Eigandi fjarlægður');
        this.handleShareCollection(sharingCollection); // Refresh
      })
      .catch((error) => {
        this.setState({ loadingSharing: false });
        this.showError('Ekki tókst að fjarlægja eiganda', error);
      });
  };

  listCollections = () => {
    this.setState({ loadingCollections: true });
    this.transcriptService
      .listCollections()
      .then((collections) => {
        this.setState({
          collections: collections.map((c) => c.toObject()),
          loadingCollections: false,
        });
      })
      .catch((error) => {
        this.setState({ loadingCollections: false });
        console.error('listCollections error', error);
      });
  };

  createCollection = (displayName: string) => {
    this.transcriptService
      .createCollection(displayName)
      .then(() => {
        message.success('Safn búið til');
        this.listCollections();
      })
      .catch((error) => {
        console.error('createCollection error', error);
      });
  };

  handleRenameCollection = (collection: transcript_pb.Collection.AsObject, newDisplayName: string) => {
    this.transcriptService
      .updateCollection(collection.name, newDisplayName)
      .then(() => {
        message.success('Nafni safns breytt');
        this.listCollections();
      })
      .catch((error) => {
        this.showError('Ekki tókst að endurnefna safn', error);
        console.error('updateCollection error', error);
      });
  };

  handleDeleteCollection = (collection: transcript_pb.Collection.AsObject) => {
    this.transcriptService
      .deleteCollection(collection.name)
      .then(() => {
        message.success('Safni var eytt');
        if (this.props.collectionName === collection.name) {
          this.props.history.push('/');
        }
        this.listCollections();
      })
      .catch((error) => {
        this.showError('Ekki tókst að eyða safni', error);
        console.error('deleteCollection error', error);
      });
  };

  addSelectedToCollection = () => {
    const { selectedTranscriptKeys, targetCollectionName } = this.state;
    if (!targetCollectionName) return;

    const promises = selectedTranscriptKeys.map((key) => {
      return this.transcriptService.addTranscriptToCollection(targetCollectionName, key as string);
    });

    Promise.all(promises)
      .then(() => {
        message.success('Upptökum bætt í safn');
        this.setState({
          selectedTranscriptKeys: [],
          isAddToCollectionModalVisible: false,
          targetCollectionName: null,
        });
        this.watchTranscripts(); // Refresh list to exclude them
      })
      .catch((err) => {
        console.error(err);
        this.showError('Ekki tókst að bæta öllum upptökum í safn', err);
      });
  };

  componentDidMount() {
    this.watchTranscripts();
    this.listCollections();
  }

  componentDidUpdate(prevProps: TranscriptsListWrapperProps, prevState: TranscriptsListWrapperState) {
    if (!prevProps.authCtx.accessToken && this.props.authCtx.accessToken) {
      this.watchTranscripts();
    } else if (prevProps.authCtx.accessToken && !this.props.authCtx.accessToken) {
      this.cancelStream();
    }
    if (prevProps.filter !== this.props.filter || prevProps.collectionName !== this.props.collectionName) {
      this.cancelStream();
      this.watchTranscripts();
    }
  }

  componentWillUnmount() {
    this.cancelStream();
  }

  handleSort = (
    sortBy: TranscriptsListWrapperState['sortBy'],
    sortDirection: TranscriptsListWrapperState['sortDirection'],
  ) => {
    this.setState({ sortBy, sortDirection });
  };

  render() {
    const {
      transcripts,
      sortBy,
      sortDirection,
      deleteError,
      collections,
      selectedTranscriptKeys,
      isAddToCollectionModalVisible,
      loadingCollections,
      isShareModalVisible,
      sharingCollection,
      sharingActors,
      newSharingEmail,
      loadingSharing,
    } = this.state;

    const { collectionName } = this.props;
    const currentCollection = collections.find((c) => c.name === collectionName);
    const displayCollectionName = currentCollection?.displayName || collectionName;

    const sharingInput = newSharingEmail.trim();
    const isSharingInputEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sharingInput);
    const isSharingInputKennitala = /^\d{10}$/.test(sharingInput);
    const isSharingInputValid = isSharingInputEmail || isSharingInputKennitala;

    return (
      <div className='transcripts-list-wrapper'>
        <CollectionsList
          collections={collections}
          loading={loadingCollections}
          selectedCollectionName={collectionName}
          onCollectionClick={(c) => {
            const pathName = c.name.replace('collections/', '');
            if (collectionName === c.name) {
              this.props.history.push('/');
            } else {
              this.props.history.push(`/collections/${pathName}`);
            }
          }}
          onCreateCollection={this.createCollection}
          onRenameCollection={this.handleRenameCollection}
          onDeleteCollection={this.handleDeleteCollection}
          onShareCollection={this.handleShareCollection}
        />

        {collectionName && (
          <div style={{ marginBottom: 16 }}>
            <Space>
              <span>
                Sýnir upptökur í safni: <strong>{displayCollectionName}</strong>
              </span>
              <Button size='small' onClick={() => this.props.history.push('/')}>
                Sýna allar (utan safna)
              </Button>
            </Space>
          </div>
        )}

        {selectedTranscriptKeys.length > 0 && (
          <div className='bulk-actions-bar' style={{ marginBottom: 16 }}>
            <Space>
              <span>{selectedTranscriptKeys.length} upptökur valdar</span>
              <Button type='primary' onClick={() => this.setState({ isAddToCollectionModalVisible: true })}>
                Bæta í safn
              </Button>
              <Button onClick={() => this.setState({ selectedTranscriptKeys: [] })}>Hætta við</Button>
            </Space>
          </div>
        )}

        <TranscriptsList
          transcripts={transcripts}
          sortBy={sortBy}
          sortDirection={sortDirection}
          handleSort={this.handleSort}
          onDelete={this.deleteTranscript}
          onRemoveFromCollection={collectionName ? this.handleRemoveFromCollection : undefined}
          onUpdateMetadata={this.updateTranscriptMetadata}
          deleteError={deleteError}
          selectedRowKeys={selectedTranscriptKeys}
          onSelectionChange={(keys) => this.setState({ selectedTranscriptKeys: keys })}
        />

        <Modal
          title='Bæta í safn'
          open={isAddToCollectionModalVisible}
          onOk={this.addSelectedToCollection}
          onCancel={() => this.setState({ isAddToCollectionModalVisible: false })}
          okText='Bæta við'
          cancelText='Hætta við'
        >
          <p>Veldu safn til að bæta völdum upptökum í:</p>
          <Select
            style={{ width: '100%' }}
            placeholder='Veldu safn'
            onChange={(value) => this.setState({ targetCollectionName: value })}
          >
            {collections.map((c) => (
              <Select.Option key={c.name} value={c.name}>
                {c.displayName || c.name}
              </Select.Option>
            ))}
          </Select>
        </Modal>

        <Modal
          title={`Deila safni: ${sharingCollection?.displayName || sharingCollection?.name}`}
          open={isShareModalVisible}
          onCancel={() => this.setState({ isShareModalVisible: false })}
          footer={[
            <Button key='close' onClick={() => this.setState({ isShareModalVisible: false })}>
              Loka
            </Button>,
          ]}
        >
          <div style={{ marginBottom: 16 }}>
            <p>Bæta við nýjum eiganda (netfang eða kennitala):</p>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder='netfang@tiro.is eða 1234567890'
                value={newSharingEmail}
                status={sharingInput && !isSharingInputValid ? 'error' : undefined}
                onChange={(e) => this.setState({ newSharingEmail: e.target.value })}
                onPressEnter={this.handleAddSharingActor}
              />
              <Button
                type='primary'
                onClick={this.handleAddSharingActor}
                loading={loadingSharing}
                disabled={!isSharingInputValid}
              >
                Bæta við
              </Button>
            </Space.Compact>
          </div>
          <p>Núverandi eigendur:</p>
          <List
            size='small'
            loading={loadingSharing}
            dataSource={sharingActors}
            renderItem={(actor) => {
              const isMe = actor.name === this.props.authCtx.userDetails?.uid;
              const hasDisplayName = !!actor.displayName && actor.displayName !== actor.name;

              return (
                <List.Item
                  actions={[
                    <Button
                      type='text'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => this.handleRemoveSharingActor(actor.name)}
                      title='Fjarlægja eiganda'
                      disabled={isMe}
                    />,
                  ]}
                >
                  <Space>
                    <span>
                      {hasDisplayName ? actor.displayName : actor.email || actor.name}
                      {hasDisplayName && actor.email && ` (${actor.email})`}
                    </span>
                    {isMe && <Tag color='blue'>þú</Tag>}
                  </Space>
                </List.Item>
              );
            }}
          />
        </Modal>
      </div>
    );
  }
}

// TODO(rkjaran): Fix reference warning. Add type annotation?
export default withRouter(withAuth(TranscriptsListWrapper));
